import { Typography } from '@shared/ui';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { remarkGroupedCards, remarkSingleCard } from '../index';
import { normalizeSlackToMarkdown } from '../utils/slackish';
import { CardBlockquote } from './CardBlockquote';
import '../../../styles/cards.css';

export type SlackishMarkdownProps = {
  text: string;
  className?: string;
  resolveMention?: (id: string) => string;
  mentionMap?: Record<string, string>;
};

// Customize sanitization schema to allow divs with class attributes
// This is safe as we only allow the class attribute (no inline styles, JS, or event handlers)
const customSchema: typeof defaultSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'div', // Explicitly allow div tags
  ],
  attributes: {
    ...defaultSchema.attributes,
    div: ['class', 'className'], // Allow class attribute on divs (both HTML 'class' and React 'className')
  },
};

const SlackishMarkdown: React.FC<SlackishMarkdownProps> = ({
  text,
  className,
  resolveMention,
  mentionMap,
}) => {
  const markdown = React.useMemo(() => {
    const resolver =
      resolveMention || (mentionMap ? (id: string) => `@${mentionMap[id] || id}` : undefined);
    return normalizeSlackToMarkdown(
      text || '',
      resolver ? { resolveMention: resolver } : undefined
    );
  }, [text, resolveMention, mentionMap]);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkGroupedCards, // Transform grouped-cards HTML first
          remarkSingleCard, // Then detect single emoji-prefixed cards
        ]}
        rehypePlugins={[
          rehypeRaw, // Parse HTML in markdown
          [rehypeSanitize, customSchema], // Then sanitize it with our custom schema
        ]}
        components={{
          blockquote({ children }) {
            return <CardBlockquote>{children}</CardBlockquote>;
          },
          div({ node, className: divClassName, children, ...props }) {
            // Handle grouped-cards container (from remarkGroupedCards)
            if (divClassName === 'grouped-cards') {
              return (
                <div
                  className="grouped-cards"
                  role="group"
                  aria-label="Related summary cards"
                  {...props}
                >
                  {children}
                </div>
              );
            }

            // Handle single card blocks (from remarkSingleCard)
            // Classes will be like: 'card-block card-win', 'card-block card-warning', etc.
            if (typeof divClassName === 'string' && divClassName.includes('card-block')) {
              // Extract card type from className (e.g., 'card-win' → 'win')
              const cardTypeMatch = divClassName.match(/card-(win|warning|quote|note)/);
              const cardType = cardTypeMatch ? cardTypeMatch[1] : undefined;

              return (
                <div
                  className={divClassName}
                  data-card-type={cardType}
                  role="note"
                  aria-label={cardType ? `${cardType} card` : 'card'}
                  {...props}
                >
                  <div className="card-content">{children}</div>
                </div>
              );
            }

            // Standard div rendering for other cases
            return (
              <div className={divClassName} {...props}>
                {children}
              </div>
            );
          },
          h3({ children }) {
            return (
              <Typography variant="overline" color="text.secondary">
                {children}
              </Typography>
            );
          },
          p({ children }) {
            return (
              <Typography variant="body2" color="text.secondary">
                {children}
              </Typography>
            );
          },
          ul({ children }) {
            return <ul className="list-disc pl-5 m-0">{children}</ul>;
          },
          li({ children }) {
            return (
              <li>
                <Typography component="div" variant="body2" color="text.secondary">
                  {children}
                </Typography>
              </li>
            );
          },
          a({ href, children }) {
            const safeHref = typeof href === 'string' ? href : undefined;
            return (
              <a href={safeHref} target="_blank" rel="noopener noreferrer" className="underline">
                {children}
              </a>
            );
          },
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default SlackishMarkdown;
